// Hand-written content for the Porto city page (/guides/portugal/porto/). See lisbon.js for how the fields work.
// Facts: from the guides' own stop lists, or well-established history. No prices, hours or restaurants.
// The port-wine guide is filed under "Vila Nova de Gaia", across the river: the alias brings it onto this page.

const porto = {
  name: 'Porto',
  aliases: ['Porto', 'Oporto', 'Vila Nova de Gaia'],
  heroImage: 'guide:discover',
  heroSample: 'riverside',
  heroTagline: 'Granite, tiles and the Douro, told on the spot.',
  lead: '{n} self-guided audio tours for Porto: the riverside, the old town, São Bento, the port lodges, Serralves and the city museums. Play them on your phone, walk at your own pace, and ask the guide anything along the way.',

  themes: [
    ['start', 'City-wide guide'],
    ['walks', 'Walks'],
    ['museums', 'Museums & art'],
  ],
  startHere: ['discover', 'riverside', 'clerigos'],

  guides: [
    { key: 'discover', theme: 'start', title: 'Discover Porto',
      versions: { en: 'discover-porto-a-journey-through-history-and-culture' },
      blurb: 'The essentials in one guide: Clérigos Tower, Livraria Lello, Bolhão market, Café Majestic, São Bento station, the cathedral, Palácio da Bolsa, Ribeira, the Dom Luís I bridge and the port cellars of Gaia.' },

    { key: 'riverside', theme: 'walks', title: 'Porto Riverside',
      versions: { en: 'porto-riverside', pt: 'porto-ribeira' },
      blurb: 'Down by the Douro: Casa do Infante, São Francisco church, Praça da Ribeira, the quays, the Barredo quarter and across the Dom Luís I bridge to Gaia.' },
    { key: 'clerigos', theme: 'walks', title: 'Clérigos & Vitória',
      versions: { en: 'clerigos-vitoria', pt: 'clerigos-e-vitoria' },
      blurb: 'Clérigos Tower, Livraria Lello, the tiled Carmo church, the Cordoaria garden, the Vitória viewpoint and the streets of the medieval Jewish quarter.' },
    { key: 'bolhao', theme: 'walks', title: 'Bolhão Market & Traditional Commerce',
      versions: { en: 'bolhao-market-traditional-commerce', pt: 'mercado-do-bolhao-e-comercio-tradicional' },
      blurb: 'The Bolhão market inside and out, the tiled Capela das Almas, an old grocery, Rua de Santa Catarina and Café Majestic.' },
    { key: 'saobento', theme: 'walks', title: 'São Bento Station & Azulejos',
      versions: { en: 'sao-bento-station-azulejos', pt: 'estacao-de-sao-bento-e-azulejos' },
      blurb: "Porto's railway station and its 20,000 tiles: the entrance hall and its panels, from medieval battles to the first trains." },
    { key: 'gaia', theme: 'walks', title: 'Vila Nova de Gaia Port Wine Trail',
      versions: { en: 'vila-nova-de-gaia-port-wine-trail', pt: 'rota-dos-vinhos-do-porto-de-vila-nova-de-gaia' },
      blurb: "Across the river: the Serra do Pilar monastery and its round church, the quays and the port wine lodges, from Sandeman and Taylor's to Graham's and Ferreira." },

    { key: 'serralves', theme: 'museums', title: 'Serralves',
      versions: { pt: 'serralves' },
      partner: 'https://www.guidesofia.com/case-studies/serralves-porto/',
      blurb: "The pink Art Deco villa, its Lalique skylight and pink bathroom, the hidden chapel, the gardens, the lake, the farm and Siza's museum." },
    { key: 'sculptures', theme: 'museums', title: 'Sculptures in the Serralves Park',
      versions: { pt: 'esculturas-no-parque-de-serralves' },
      partner: 'https://www.guidesofia.com/case-studies/serralves-porto/',
      blurb: 'Fourteen sculptures in the Serralves park, from Richard Serra and Claes Oldenburg to Anish Kapoor, Olafur Eliasson and Álvaro Siza.' },
    { key: 'soilart', theme: 'museums', title: 'Soil Art Tales',
      versions: { pt: 'soil-art-tales-historias-de-arte-e-solo' },
      blurb: 'An art route about the ground under your feet, with works by Miguel Teodoro, Binta Diaw, Nikki Lindt and Jo Pearl.' },
    { key: 'soaresdosreis', theme: 'museums', title: 'Museu Nacional Soares dos Reis',
      versions: { pt: 'museu-nacional-soares-dos-reis' },
      blurb: "The national museum in the Carrancas palace: Wellington's stolen dinner, a museum born under siege, Soares dos Reis' sculpture, ceramics and treasures from Asia." },
    { key: 'military', theme: 'museums', title: 'Military Museum of Porto',
      versions: { en: 'military-museum-of-porto', pt: 'museu-militar-do-porto' },
      blurb: "From the Peninsular War and the Liberal Wars to the political police and the colonial war, with an arsenal of cannons and 16,000 miniature soldiers." },
  ],

  areas: [
    { name: 'Ribeira & the river', image: 'guide:riverside', text: "The old merchant quarter on the Douro. Porto's historic centre has been UNESCO World Heritage since 1996.",
      guides: ['riverside', 'discover'] },
    { name: 'Clérigos & Vitória', text: 'The upper town around the Clérigos Tower, Livraria Lello and the Cordoaria garden, with the old Jewish quarter on the slope below.',
      guides: ['clerigos'] },
    { name: 'Downtown: São Bento & Bolhão', text: 'The busy centre around the station, the market and the shopping street of Santa Catarina.',
      guides: ['saobento', 'bolhao', 'military'] },
    { name: 'Vila Nova de Gaia', image: 'guide:gaia', text: 'Across the Dom Luís I bridge, the port wine lodges line the south bank of the Douro.',
      guides: ['gaia'] },
    { name: 'Serralves & the west', text: "In the west of the city, the Serralves Foundation combines Álvaro Siza's museum of contemporary art, an Art Deco villa and a large park.",
      guides: ['serralves', 'sculptures'] },
  ],

  timeline: [
    { year: '1387', title: 'A royal wedding', text: 'King João I marries Philippa of Lancaster in Porto Cathedral, sealing the alliance with England.', guide: 'discover' },
    { year: '1394', title: 'Henry the Navigator is born', text: 'The prince who launched the age of Portuguese exploration is born in Porto, by tradition in the Casa do Infante.', guide: 'riverside' },
    { year: '1756', title: 'The Douro is demarcated', text: 'The Marquis of Pombal draws the limits of the port wine region, one of the first demarcated wine regions in the world.', guide: 'gaia' },
    { year: '1809', title: 'Wellington takes back Porto', text: 'British and Portuguese troops cross the Douro and drive out the French army.', guide: 'soaresdosreis' },
    { year: '1832', title: 'The Siege of Porto', text: 'In the civil war between liberals and absolutists, the city holds out for a year under siege.', guide: 'military' },
    { year: '1886', title: 'The Dom Luís I bridge', text: 'The double-deck iron bridge opens between Porto and Gaia.', guide: 'riverside' },
    { year: '1916', title: 'São Bento station', text: "The station opens, its hall covered with Jorge Colaço's tile panels.", guide: 'saobento' },
  ],
  timelineTitle: ['Porto in ', 'seven moments'],

  plan: {
    title: ['Two days in Porto ', 'with our guides'],
    intro: 'The old town and the river on day one, port wine and Serralves on day two.',
    days: [
      { label: 'Day 1', title: 'The old town and the river', stops: [['clerigos', 'in the morning'], ['saobento', 'the tiled station'], ['riverside', 'down to the Douro for the evening']] },
      { label: 'Day 2', title: 'Port wine and art', stops: [['gaia', 'across the bridge'], ['serralves', 'in the afternoon'], ['bolhao', 'or the market and Café Majestic']] },
    ],
    dayTrip: { city: 'portugal/lisbon', text: 'Heading south? Lisbon is about three hours away by train.', link: 'See the Lisbon guides' },
    tips: [
      ['Wear shoes with grip', 'Porto is steep, with granite pavements that get slippery in the rain.'],
      ['Walk the top deck', 'The upper deck of the Dom Luís I bridge is open to walkers and the metro, with the best view of the river.'],
      ['Use the Guindais funicular', 'It climbs from the riverside up to the old walls, and saves you the steepest stairs.'],
      ['Download before you go', 'Download your guides over Wi-Fi. They play outdoors with GPS and no mobile data.'],
    ],
  },

  extraFaq: [
    ['How many days do I need for Porto?', 'Two days is enough for the old town, the river, the port lodges in Gaia and Serralves. Add a day if you want to slow down or visit the museums.'],
  ],
  startAnswer: 'Start with Discover Porto: it covers Clérigos, Lello, Bolhão, São Bento, the cathedral, Ribeira and the port cellars in one walk.',

  partnerBox: {
    text: "Serralves already works with GuideSofia. We turn the texts you already have into an audio guide on the visitor's own phone, approved by your team.",
    caseStudy: { url: 'https://www.guidesofia.com/case-studies/serralves-porto/', label: 'Read the Serralves case study' },
  },
};

export default porto;
